'use client';

import cx from 'classix';
import { useAtomValue } from 'jotai/index';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import {
  useEffect,
  useState,
} from 'react';
import { useIsMounted } from 'usehooks-ts';

import getRandomBlogPost from '@/features/blog/requests/get-random-blog-post';
import { type BlogPost } from '@/features/blog/types/blog-post-types';
import { listingsViewAtom } from '@/features/listing/atoms/listings-view-atom';
import { LISTING_VIEW_VARIANTS } from '@/features/listing/constants/listing-constants';
import { getCookieCurrentPlace } from '@/features/place/cookies/cookie-current-place';
import { Link } from '@/i18n/routing';
import Button from '@/ui/button/button';
import Skeleton from '@/ui/skeleton/skeleton';

import styles from './blog-card.module.scss';

export default function BlogCard() {
  const listingsView = useAtomValue(listingsViewAtom);
  const defaultLink = 'https://reactdev.roomster.com/blog';

  const defaultPost: BlogPost = {
    description: 'On our blog you can find tips and news about co-living, rooms for rent and roommates search.',
    id: 'defaultPost',
    imgUrl: '/images/blog/landing-offer-image-1.svg',
    link: defaultLink,
    title: 'Visit our blog',
    type: 'blogPost',
  };

  const [blogPost,
    setBlogPost] = useState<BlogPost>();
  const [isLoading,
    setIsLoading] = useState<boolean>(true);
  const isMounted = useIsMounted();

  useEffect(() => {
    const currentPlace = getCookieCurrentPlace();

    if (currentPlace?.fullName) {
      getRandomBlogPost(currentPlace.fullName)
        .then((post) => { setBlogPost(post); })
        .catch(() => { setBlogPost(defaultPost); })
        .finally(() => { setIsLoading(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  const translationsListingBlog = useTranslations('blog');

  return (
    <div className={cx(styles.blogCard, styles[`blogCard__${listingsView}`])}>
      {(isLoading && (
        <div className={styles.blogCard_skeleton}>
          <Skeleton />
        </div>
      ))}

      {(!isLoading && blogPost) && (
      <Link prefetch={false} target="_blank" href={blogPost.link} className={styles.blogCard_link}>
        <div className={styles.blogCard_imageContainer}>
          <Image
            className={styles.blogCard_image}
            src={blogPost.imgUrl ?? '/images/blog/landing-offer-image-1.svg'}
            alt={blogPost.title}
            fill
            unoptimized
          />
        </div>

        <div className={styles.blogCard_body}>
          <div className={styles.blogCard_content}>
            <div className={styles.blogCard_title}>{blogPost.title}</div>
            <div className={styles.blogCard_description}>{blogPost.description}</div>
          </div>

          <div className={styles.blogCard_action}>
            <Button
              element="button"
              size={listingsView === LISTING_VIEW_VARIANTS.grid ? 'md' : 's'}
              variant="outlined"
              fontSize="s"
              fullWidth
            >
              {translationsListingBlog('read-more')}
            </Button>
          </div>
        </div>
      </Link>
      )}
    </div>
  );
}
